import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { Form as FinalForm } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { Col, Row } from "reactstrap";
import AppBar from "src/components/AppBar/AppBar";
import { Container } from "src/components/Container/Container";
import SingleSelectField from "src/components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import { IconName } from "src/components/Icon/Icon";
import Toast from "src/components/Toast/Toast";
import { IUser } from "src/store/models/user.model";
import { createUser, fetchUser, updateUser } from "src/store/reducers/users";
import { fetchUserOptions } from "src/store/reducers/usersBuilder";
import { handlePasswordUpdate } from "src/util/util.PasswordUpdate";
import { addDefaultOption } from "src/util/utils.defaultValues";
import * as S from "../../constants/StringConstants";
import "./UserBuilder.scss";

interface MatchParams {
  userId?: string;
}

interface LocationState {
  user?: IUser;
}

interface UserBuilderProps extends RouteComponentProps<MatchParams, any, LocationState> {}

const UserBuilder: React.FC<UserBuilderProps> = ({ location }) => {
  const [user, setUser] = useState(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const userID = location.state?.user.userID || null;
  const isEditMode = !!userID;
  const dispatch = useDispatch();
  const { userRoleOptions, branchOptions, rbmOptions } = useSelector((state: any) => state.users);

  const handleProfileSubmit = async (values: any) => {
    try {
      if (isEditMode) {
        const updatedUser = { ...values, userID };
        await dispatch(updateUser(updatedUser));
        setToastMessage(S.U_PROFILE_SUCCESS);
      } else {
        await dispatch(createUser(values));
        setToastMessage(S.U_CREATE_SUCCESS);
      }
    } catch (err) {
      setToastMessage(S.U_CREATE_UPDATE_ERROR);
    }
  };

  const handlePasswordSubmit = async (values: any, form: any) => {
    await handlePasswordUpdate(userID.toString(), values, setToastMessage, form);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (userID) {
        const response: any = await dispatch(fetchUser(userID.toString()));
        setUser(get(response, "value.data.user"));
      }
    };

    fetchUserData();
  }, [userID]);

  useEffect(() => {
    dispatch(fetchUserOptions());
  }, [dispatch]);

  const branchList = addDefaultOption("", branchOptions) || [];
  const rbmList = addDefaultOption("", rbmOptions) || [];

  return (
    <>
      <AppBar icon={IconName.User} title={S.U_DETAIL_PAGE_TITLE} />
      <section className="user-builder__wrap">
        <Container padding="3rem">
          <Row>
            <Col className="app-form-header">
              <h2 className="form-title">{S.U_USER_INFORMATION}</h2>
            </Col>
          </Row>

          <FinalForm
            onSubmit={handleProfileSubmit}
            initialValues={user || {}}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md="4">
                    <TextField label={S.U_FIRST_NAME_LABEL} name="firstName" required />
                  </Col>
                  <Col md="4">
                    <TextField label={S.U_LAST_NAME_LABEL} name="lastName" required />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md="4">
                    <TextField label={S.U_EMAIL_LABEL} name="email" type="email" required />
                  </Col>
                  <Col md="4">
                    {!isEditMode && <TextField label={S.U_PASSWORD_LABEL} name="password" type="password" required />}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md="4">
                    <SingleSelectField
                      label={S.U_BRANCH_TYPE_LABEL}
                      key="user.branchTypeID-key"
                      name="branchTypeID"
                      options={branchList}
                    />
                  </Col>
                  <Col md="4">
                    <SingleSelectField
                      label={S.U_RBM_LABEL}
                      key="user.rbmManagerId-key"
                      name="rbmManagerId"
                      options={rbmList}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md="4">
                    <SingleSelectField label="Role" name="userRole" options={userRoleOptions} isRequired={true} />
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col className="user-builder__button-wrap">
                    <button className="button-v2 button-v2__primary" type="submit">
                      {S.U_SUBMIT_BUTTON_PROFILE}
                    </button>
                  </Col>
                </Row>
              </form>
            )}
          />

          {isEditMode && (
            <>
              <hr className="my-5" />
              <Row>
                <Col className="app-form-header">
                  <h2 className="form-title">{S.U_PASSWORD_INFORMATION}</h2>
                </Col>
              </Row>
              <FinalForm
                onSubmit={handlePasswordSubmit}
                render={({ handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                      <Col sm="4">
                        <TextField label={S.U_NEW_PASSWORD_LABEL} name="newPassword" type="password" required />
                      </Col>
                      <Col sm="4">
                        <TextField label={S.U_CONFIRM_PASSWORD_LABEL} name="confirmPassword" type="password" required />
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col className="user-builder__button-wrap">
                        <button className="button-v2 button-v2__primary" type="submit">
                          {S.U_SUBMIT_BUTTON_PASSWORD}
                        </button>
                      </Col>
                    </Row>
                  </form>
                )}
              />
            </>
          )}
        </Container>
      </section>

      <Toast message={toastMessage} onClose={() => setToastMessage(null)} open={!!toastMessage} />
    </>
  );
};

export default UserBuilder;
